import { Feriado, TipoPrazoRelativo } from '../models/ciclo';

// O backend serializa DateTime como ISO completo ("2026-10-31T00:00:00"), não só a data — sem
// descartar a parte de hora antes do split('-'), o terceiro pedaço vira "31T00:00:00" e Number()
// disso é NaN, propagando "Invalid Date"/"NaN-NaN-NaN" por toda essa função ida e volta.
const toDate = (iso: string): Date => {
  const [ano, mes, dia] = iso.split('T')[0].split('-').map(Number);
  return new Date(ano, (mes ?? 1) - 1, dia ?? 1);
};

const toIso = (date: Date): string => {
  const ano = date.getFullYear();
  const mes = String(date.getMonth() + 1).padStart(2, '0');
  const dia = String(date.getDate()).padStart(2, '0');
  return `${ano}-${mes}-${dia}`;
};

export const ehFimDeSemana = (iso: string): boolean => {
  const dia = toDate(iso).getDay();
  return dia === 0 || dia === 6;
};

export const ehFeriado = (iso: string, feriados: Feriado[], empresaId?: number): boolean =>
  // f.data vem como ISO completo do backend (mesmo motivo do toDate acima) — sem o split('T')[0],
  // essa comparação nunca batia e todo cálculo de dia útil ignorava feriados silenciosamente.
  feriados.some(f => f.data.split('T')[0] === iso && (f.tipo !== 'EMPRESA' || f.empresaId === empresaId));

export const ehDiaUtil = (iso: string, feriados: Feriado[], empresaId?: number): boolean =>
  !ehFimDeSemana(iso) && !ehFeriado(iso, feriados, empresaId);

export const proximoDiaUtil = (iso: string, feriados: Feriado[], empresaId?: number): string => {
  let data = toDate(iso);
  let candidato = toIso(data);
  while (!ehDiaUtil(candidato, feriados, empresaId)) {
    data.setDate(data.getDate() + 1);
    candidato = toIso(data);
  }
  return candidato;
};

export const somarDiasUteis = (iso: string, quantidade: number, feriados: Feriado[], empresaId?: number): string => {
  const data = toDate(iso);
  const passo = quantidade >= 0 ? 1 : -1;
  let restante = Math.abs(quantidade);
  while (restante > 0) {
    data.setDate(data.getDate() + passo);
    if (ehDiaUtil(toIso(data), feriados, empresaId)) restante -= 1;
  }
  return toIso(data);
};

export const diasUteisEntre = (inicioIso: string, fimIso: string, feriados: Feriado[], empresaId?: number): number => {
  let contador = 0;
  const data = toDate(inicioIso);
  const fim = toDate(fimIso);
  while (data <= fim) {
    if (ehDiaUtil(toIso(data), feriados, empresaId)) contador += 1;
    data.setDate(data.getDate() + 1);
  }
  return contador;
};

export const listarDiasUteisNoIntervalo = (inicioIso: string, fimIso: string, feriados: Feriado[], empresaId?: number): string[] => {
  const dias: string[] = [];
  const data = toDate(inicioIso);
  const fim = toDate(fimIso);
  while (data <= fim) {
    const iso = toIso(data);
    if (ehDiaUtil(iso, feriados, empresaId)) dias.push(iso);
    data.setDate(data.getDate() + 1);
  }
  return dias;
};

const MAPA_PRAZO_RELATIVO: Record<TipoPrazoRelativo, number> = {
  'D-5': -5,
  'D-3': -3,
  'D-1': -1,
  'D+1': 1,
  'D+2': 2,
  'D+5': 5,
};

export interface ResultadoPrazoRelativo {
  dataOriginal: string;
  dataAjustada: string;
  motivo?: string;
  proximoDiaUtilAplicado?: string;
}

export const calcularPrazoRelativo = (
  dataBase: string,
  tipo: TipoPrazoRelativo,
  feriados: Feriado[],
  empresaId?: number
): ResultadoPrazoRelativo => {
  const dataOriginal = somarDiasUteis(dataBase, MAPA_PRAZO_RELATIVO[tipo], feriados, empresaId);
  if (ehDiaUtil(dataOriginal, feriados, empresaId)) {
    return { dataOriginal, dataAjustada: dataOriginal };
  }
  const dataAjustada = proximoDiaUtil(dataOriginal, feriados, empresaId);
  const motivo = ehFimDeSemana(dataOriginal) ? 'Data original cai em fim de semana' : 'Data original cai em feriado';
  return { dataOriginal, dataAjustada, motivo, proximoDiaUtilAplicado: dataAjustada };
};
